import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { getDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import {
  CAL_SOURCES, fetchCalendar, fetchParticipants, iso, startOfMonth, endOfMonth,
} from '../schoolCalendar';
import { ChevronLeft, ChevronRight, RefreshCw, Users, X } from 'lucide-react';
import { useT } from '../i18n.jsx';

/**
 * The school calendar, assembled from the platform's own records by schoolCalendar.js.
 *
 * A month grid with a day panel beside it, used both on the dashboard and in School Calendar. One
 * component rather than two because the only difference is how much room it has: two would be two
 * copies of the source colours and labels, and the day they disagree is the day somebody trusts the
 * wrong one.
 *
 * Detail opens in a dialog rather than expanding in place. A month cell cannot hold it, and an
 * agenda row that grows when clicked pushes everything below it down -- which on a dashboard means
 * the thing you were about to read walks away from the pointer.
 */

const chip = (on) => ({
  padding: '0.22rem 0.6rem',
  borderRadius: 'var(--radius-md)',
  fontSize: '0.75rem',
  fontWeight: on ? '700' : '500',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  flexShrink: 0,
  display: 'inline-flex',
  alignItems: 'center',
  backgroundColor: on ? 'var(--color-nav-active)' : 'transparent',
  border: `1px solid ${on ? 'var(--color-accent)' : 'var(--color-border)'}`,
  color: on ? 'var(--color-text-main)' : 'var(--color-text-subtle)',
});

const SchoolAgenda = ({ schoolId, email, isMobile, compact = false }) => {
  const { t, locale } = useT();
  const sourceLabel = (id) => t(`agenda.sources.${id}`, {}, CAL_SOURCES[id].label);
  const [ownInitial, setOwnInitial] = useState('');
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [anchor, setAnchor] = useState(() => new Date());
  const [scope, setScope] = useState('all');
  const [off, setOff] = useState([]);
  const [openDay, setOpenDay] = useState(null);
  const [detail, setDetail] = useState(null);
  const [people, setPeople] = useState({});
  const [reload, setReload] = useState(0);

  // Duty records name people by initial as well as by address, so both are needed to answer "is this
  // mine" for anything written before the addresses were stamped on.
  useEffect(() => {
    if (!schoolId || !email) return;
    getDoc(doc(db, 'schools', schoolId, 'staff_profiles', email))
      .then(s => setOwnInitial(s.exists() ? String(s.data().initial || '') : ''))
      .catch(() => setOwnInitial(''));
  }, [schoolId, email]);

  const from = iso(startOfMonth(anchor));
  const to = iso(endOfMonth(anchor));

  useEffect(() => {
    if (!schoolId) return undefined;
    let cancelled = false;
    setLoading(true);
    fetchCalendar({ schoolId, from, to, email, initial: ownInitial, t })
      .then(list => { if (!cancelled) { setEntries(list); setLoading(false); } })
      .catch(e => { console.error('calendar', e); if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [schoolId, from, to, email, ownInitial, reload]);

  // Whole-school entries survive the Mine filter. Filtering to your own day and losing the fact that
  // the school is closed would make the filter actively misleading.
  const shown = useMemo(() => entries.filter(e => (
    !off.includes(e.source) && (scope === 'all' || e.mine || e.wholeSchool)
  )), [entries, off, scope]);

  const byDay = useMemo(() => {
    const map = new Map();
    shown.forEach(e => {
      if (!map.has(e.date)) map.set(e.date, []);
      map.get(e.date).push(e);
    });
    return map;
  }, [shown]);

  const openDetail = useCallback(async (entry) => {
    setDetail(entry);
    if (people[entry.key]) return;
    // Loaded on opening, never before. A month of activities would otherwise be a participant query
    // per activity before a single cell appeared.
    setPeople(p => ({ ...p, [entry.key]: 'loading' }));
    try {
      const list = await fetchParticipants(schoolId, entry);
      setPeople(p => ({ ...p, [entry.key]: list }));
    } catch (e) {
      console.error('participants', e);
      setPeople(p => ({ ...p, [entry.key]: [] }));
    }
  }, [people, schoolId]);

  useEffect(() => {
    if (!openDay) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (detail) setDetail(null); else setOpenDay(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openDay, detail]);

  const today = iso(new Date());
  const titleLines = compact ? 2 : 3;
  // A floor, not a height. The rows take an equal share of whatever the parent gives them, and this
  // is what stops them collapsing when the parent gives them almost nothing -- at which point the
  // grid scrolls instead, rather than becoming seven columns of unreadable slivers.
  const cellMin = compact ? '64px' : (isMobile ? '62px' : '76px');

  const first = startOfMonth(anchor);
  const cells = [];
  for (let i = 0; i < first.getDay(); i += 1) cells.push(null);
  for (let d = 1; d <= endOfMonth(anchor).getDate(); d += 1) {
    cells.push(new Date(anchor.getFullYear(), anchor.getMonth(), d));
  }
  // Five or six depending on where the month starts. The row template is built from this so the
  // weeks divide the height evenly -- hard-coding six leaves a blank strip in most months.
  const weekRows = Math.ceil(cells.length / 7);

  const dayList = openDay ? (byDay.get(openDay) || []) : [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.7rem', height: '100%', minHeight: 0 }}>
      <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <button type="button" onClick={() => setAnchor(a => new Date(a.getFullYear(), a.getMonth() - 1, 1))} style={chip(false)} aria-label={t('agenda.previousMonth')}><ChevronLeft size={14} /></button>
        <strong style={{ fontSize: '0.9rem', minWidth: '8.5rem', textAlign: 'center' }}>
          {anchor.toLocaleDateString(locale, { month: 'long', year: 'numeric' })}
        </strong>
        <button type="button" onClick={() => setAnchor(a => new Date(a.getFullYear(), a.getMonth() + 1, 1))} style={chip(false)} aria-label={t('agenda.nextMonth')}><ChevronRight size={14} /></button>
        <button type="button" onClick={() => { setAnchor(new Date()); setOpenDay(today); setDetail(null); }} style={chip(false)}>{t('agenda.today')}</button>
        {[['all', t('agenda.everything')], ['mine', t('agenda.mine')]].map(([id, label]) => (
          <button key={id} type="button" onClick={() => setScope(id)} style={chip(scope === id)}>{label}</button>
        ))}
        <button type="button" onClick={() => setReload(n => n + 1)} disabled={loading} style={{ ...chip(false), marginLeft: 'auto' }}>
          <RefreshCw size={12} style={{ marginRight: '0.3rem' }} />{loading ? t('common.loading') : t('agenda.refresh')}
        </button>
      </div>

      {!compact && (
        <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap' }}>
          {Object.entries(CAL_SOURCES).map(([id, meta]) => {
            const on = !off.includes(id);
            return (
              <button key={id} type="button"
                      onClick={() => setOff(prev => (on ? [...prev, id] : prev.filter(x => x !== id)))}
                      style={{ ...chip(on), borderColor: on ? meta.colour : 'var(--color-border)' }}>
                <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '2px', backgroundColor: on ? meta.colour : 'transparent', border: `1px solid ${meta.colour}`, marginRight: '0.35rem' }} />
                {sourceLabel(id)}
              </button>
            );
          })}
        </div>
      )}

      {/* Full width. The day list moved into the dialog, so the grid no longer shares the row with a
          panel -- which on a dashboard meant the calendar itself got about half the space it needed
          and every cell became a row of dots. */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
        // The header row takes what it needs; the weeks share the rest equally. This is what makes
        // the calendar fill its card instead of ending halfway down it.
        gridTemplateRows: `auto repeat(${weekRows}, minmax(${cellMin}, 1fr))`,
        gap: '1px', backgroundColor: 'var(--color-border)',
        border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)',
        flex: 1, minHeight: 0, overflowY: 'auto',
      }}>
        {['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'].map(d => (
          <div key={d} style={{ backgroundColor: 'var(--color-bg-app)', padding: '0.3rem', textAlign: 'center', fontSize: '0.7rem', fontWeight: '700', color: 'var(--color-text-muted)' }}>{t(`agenda.weekdays.${d}`)}</div>
        ))}
        {cells.map((d, i) => {
          if (!d) return <div key={`x${i}`} style={{ backgroundColor: 'var(--color-bg-subtle)' }} />;
          const key = iso(d);
          const list = byDay.get(key) || [];
          const isToday = key === today;
          return (
            <button key={key} onClick={() => { setOpenDay(key); setDetail(null); }} style={{
              backgroundColor: isToday ? 'var(--color-nav-active)' : 'var(--color-bg-card)',
              border: 'none', cursor: 'pointer', textAlign: 'left', padding: '0.3rem',
              display: 'flex', flexDirection: 'column', gap: '2px', overflow: 'hidden',
            }}>
              <span style={{ fontSize: '0.72rem', fontWeight: isToday ? '700' : '500', color: isToday ? 'var(--color-primary)' : 'var(--color-text-main)' }}>
                {d.getDate()}
              </span>
              {/* Titles now that there is room for them, truncated to one line each. A cell showing
                  only dots tells you something is on and nothing about what -- which meant opening
                  every day to find out. Beyond a few they go back to being a count, because a cell
                  cannot list thirty and a truncated list of thirty is worse than a number. */}
              {list.slice(0, titleLines).map(ev => (
                <span key={ev.key} title={ev.title} style={{
                  display: 'block', fontSize: '0.68rem', lineHeight: 1.35, maxWidth: '100%',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  borderLeft: `3px solid ${CAL_SOURCES[ev.source].colour}`, paddingLeft: '3px',
                  color: 'var(--color-text-subtle)',
                }}>{ev.title}</span>
              ))}
              {list.length > titleLines && (
                <span style={{ fontSize: '0.65rem', color: 'var(--color-text-muted)' }}>
                  {t('agenda.more', { count: list.length - titleLines })}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* One dialog, two levels: the day's list, and then one entry from it. Stacking a second
          dialog on top of the first would leave two overlays and two Escape targets, and going back
          would mean guessing which one closed. Back returns to the list, Escape steps out one level
          at a time. */}
      {openDay && (
        <div onClick={() => { setDetail(null); setOpenDay(null); }} style={{ position: 'fixed', inset: 0, zIndex: 130, backgroundColor: 'var(--color-overlay)', display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center', padding: isMobile ? 0 : '2rem' }}>
          <div onClick={ev => ev.stopPropagation()} style={{ backgroundColor: 'var(--color-bg-card)', borderRadius: isMobile ? 'var(--radius-lg) var(--radius-lg) 0 0' : 'var(--radius-lg)', paddingBottom: 'env(safe-area-inset-bottom, 0px)', boxShadow: 'var(--shadow-lg)', width: '100%', maxWidth: isMobile ? '100%' : '560px', maxHeight: isMobile ? '85vh' : '80vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: isMobile ? '0.9rem 1rem' : '1.05rem 1.25rem', borderBottom: '1px solid var(--color-border)' }}>
              {detail && (
                <button type="button" onClick={() => setDetail(null)} aria-label={t('agenda.backToDay')}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', display: 'flex', padding: '0.2rem', flexShrink: 0 }}>
                  <ChevronLeft size={19} />
                </button>
              )}
              {detail && <span style={{ width: '4px', alignSelf: 'stretch', borderRadius: '2px', backgroundColor: CAL_SOURCES[detail.source].colour, flexShrink: 0 }} />}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '1rem', fontWeight: '700', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {detail ? detail.title : openDay}
                </div>
                <div style={{ fontSize: '0.77rem', color: 'var(--color-text-muted)', marginTop: '0.1rem' }}>
                  {detail
                    ? `${sourceLabel(detail.source)} \u00b7 ${detail.date}${detail.endDate && detail.endDate !== detail.date ? ` \u2013 ${detail.endDate}` : ''}`
                    : `${openDay === today ? `${t('agenda.todayPrefix')} \u00b7 ` : ''}${t('agenda.itemCount', { count: dayList.length })}`}
                </div>
              </div>
              <button type="button" onClick={() => { setDetail(null); setOpenDay(null); }} aria-label={t('common.close')}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', display: 'flex', padding: '0.2rem', flexShrink: 0 }}>
                <X size={19} />
              </button>
            </div>

            {!detail ? (
              <div style={{ overflowY: 'auto' }}>
                {dayList.length === 0 ? (
                  <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-text-muted)', fontSize: '0.85rem' }}>{t('agenda.nothingOnDay')}</div>
                ) : dayList.map(ev => (
                  <button key={ev.key} onClick={() => openDetail(ev)} style={{ width: '100%', textAlign: 'left', border: 'none', borderBottom: '1px solid var(--color-border)', background: 'transparent', cursor: 'pointer', padding: '0.6rem 0.9rem', display: 'flex', gap: '0.6rem', alignItems: 'center' }}>
                    <span style={{ width: '4px', alignSelf: 'stretch', borderRadius: '2px', backgroundColor: CAL_SOURCES[ev.source].colour, flexShrink: 0 }} />
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: 'block', fontSize: '0.88rem', fontWeight: '600' }}>{ev.title}</span>
                      <span style={{ display: 'block', fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
                        {sourceLabel(ev.source)}{ev.detail ? ` \u00b7 ${ev.detail}` : ''}
                      </span>
                    </span>
                    {ev.mine && <span style={{ fontSize: '0.65rem', fontWeight: '700', color: 'var(--color-accent)', flexShrink: 0 }}>{t('agenda.mineTag')}</span>}
                    <ChevronRight size={15} style={{ color: 'var(--color-text-muted)', flexShrink: 0 }} />
                  </button>
                ))}
              </div>
            ) : (
              <div style={{ padding: isMobile ? '0.9rem 1rem' : '1.1rem 1.25rem', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
                {detail.detail && <div style={{ fontSize: '0.88rem', lineHeight: 1.6 }}>{detail.detail}</div>}
                {detail.mine && <div style={{ fontSize: '0.8rem', fontWeight: '700', color: 'var(--color-accent)' }}>{t('agenda.youAreOnThis')}</div>}

                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.78rem', fontWeight: '700', color: 'var(--color-text-muted)', marginBottom: '0.35rem' }}>
                    <Users size={13} /> {t('agenda.whoIsOnThis')}
                  </div>
                  {people[detail.key] === 'loading' ? (
                    <span style={{ fontSize: '0.83rem', color: 'var(--color-text-muted)' }}>{t('common.loading')}</span>
                  ) : !people[detail.key] || people[detail.key].length === 0 ? (
                    <span style={{ fontSize: '0.83rem', color: 'var(--color-text-muted)' }}>
                      {/* Says which of the two it is. "Nobody" reads as an empty activity when it may
                          simply be a kind of entry that never has a list. */}
                      {['ole', 'tutorial', 'invigilation', 'patrol'].includes(detail.source)
                        ? t('agenda.nobodyListed')
                        : t('agenda.noParticipantList')}
                    </span>
                  ) : (
                    <>
                      <div style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)', marginBottom: '0.3rem' }}>
                        {people[detail.key].length}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem', fontSize: '0.85rem' }}>
                        {people[detail.key].map(pp => (
                          <div key={pp.sid}>
                            {pp.name}
                            {pp.detail ? <span style={{ color: 'var(--color-text-muted)' }}> · {pp.detail}</span> : null}
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SchoolAgenda;
